/**
 * Latches the clicked add-to-cart button into a permanent "Added" state
 * instead of showing Dawn's cart notification popup, and pulses the header
 * cart bubble so the count change still registers.
 *
 * Every item in this catalog is stock 1 / inventoryPolicy DENY (one-of-a-kind
 * consignment), so once a variant is added it can never be added again — the
 * button is meant to stay disabled forever, not reset.
 *
 * Handles both quick-add buttons in product grids (.quick-add__submit) and
 * the product page's main add-to-cart button (.product-form__submit).
 */
(function () {
  function buttonForVariant(variantId) {
    if (!variantId) return null;
    var idInput = document.querySelector(
      'product-form input[name="id"][value="' + variantId + '"]'
    );
    if (!idInput) return null;
    var form = idInput.closest('product-form');
    if (!form) return null;
    return form.querySelector('[type="submit"]');
  }

  function latch(button, label, sweep) {
    // Guard against double-application if cartUpdate fires more than once
    // for the same button, or if the page-load pass and an add overlap.
    if (!button || button.classList.contains('is-added')) return;

    button.classList.add('is-added');

    // Use the `disabled` property, not `aria-disabled`. product-form.js's
    // submit handler runs a `.finally()` right after publishing cartUpdate
    // that calls `removeAttribute('aria-disabled')` on this same button —
    // that would silently undo an aria-disabled-based lock. `disabled` is
    // never touched by that code path.
    button.disabled = true;

    var spans = button.querySelectorAll('span');
    for (var i = 0; i < spans.length; i++) {
      var span = spans[i];
      if (
        span.classList.contains('sold-out-message') ||
        span.classList.contains('icon-wrap')
      ) {
        continue;
      }
      // aria-hidden so the button's aria-live only announces the word
      span.innerHTML =
        '<svg class="tt-added-check" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path d="M4 12.5 L9.5 18 L20 6.5"' +
        (sweep ? '' : ' style="animation:none"') +
        '/></svg>' +
        '<span class="tt-added-label">' + label + '</span>';
      break;
    }

    // Hook for seasonal decoration (snippets/tt-halloween.liquid). Only the
    // shopper's own tap fires it, not the page-load "In your cart" pass.
    if (sweep) button.dispatchEvent(new CustomEvent('tt:added', { bubbles: true }));
  }

  function handleCartUpdate(event) {
    if (!event || event.source !== 'product-form') return;
    var button = buttonForVariant(event.productVariantId);
    if (!button) return;
    // "Added" confirms the tap the shopper just made, and the check sweeps in.
    latch(button, 'Added', true);
    pulseCartBubble();
  }

  /**
   * The server-rendered HTML has no idea what is already in the cart, so after
   * a reload — or the far more common case of tapping into a product and
   * hitting back — every button reads "Add to cart" again for items the
   * shopper already holds. Tapping one returns a 422, and Dawn's error path
   * permanently relabels that button "Sold out", which is both wrong and
   * alarming on a store where everything is one-of-a-kind.
   *
   * So ask the cart what is actually in it rather than inferring. Different
   * wording from a fresh add: this states a fact rather than confirming an
   * action, and the check is already drawn instead of sweeping in.
   */
  function markAlreadyInCart() {
    var cartUrl =
      (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';

    fetch(cartUrl)
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        cart.items.forEach(function (item) {
          latch(buttonForVariant(item.variant_id), 'In your cart', false);
        });
      })
      .catch(function () {
        // Purely cosmetic — if the cart can't be read, leave the buttons alone.
      });
  }

  function pulseCartBubble() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    // cart-notification.js replaces this element's innerHTML on every add,
    // but the element itself persists, so the pulse class is safe to toggle
    // here rather than re-applying it after every re-render.
    bubble.classList.remove('tt-cart-pulse');
    void bubble.offsetWidth; // force reflow so the animation restarts
    bubble.classList.add('tt-cart-pulse');
  }

  // constants.js and pubsub.js are loaded with `defer` in layout/theme.liquid
  // and this script is also deferred and appears later in the document, so
  // subscribe/PUB_SUB_EVENTS are expected to exist — guard anyway in case
  // load order ever changes.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
  }

  markAlreadyInCart();

  // /cart/add.js is a read-modify-write against a single cart document, not
  // an atomic increment. Every product-form submit fires its own fetch, so
  // firing quick-add clicks in quick succession (fast is the whole point of
  // this feature) sends overlapping requests that read the same starting
  // cart and clobber each other on write — verified on the live theme:
  // 6 quick-adds 200ms apart land only 4 items, 1400ms apart lands all 6.
  // Shopify gives us no atomic add endpoint, so the fix has to be client
  // side: force every /cart/add call through a single-file queue so only
  // one is ever in flight, which is enough to make each read-modify-write
  // safe. Buttons already latch to "Added" as soon as their own request's
  // cartUpdate fires, so a queued click just reads as a slightly longer
  // pending state — the spinner from product-form.js covers it.
  if (!window.__ttCartAddQueued) {
    window.__ttCartAddQueued = true;

    var nativeFetch = window.fetch.bind(window);
    var tail = Promise.resolve();

    // Anchored to the end of the path, not a bare substring: a bare match
    // also catches third-party beacons that carry a cart URL in their query
    // string, which would park a slow external request ahead of real adds.
    // Anchoring at the end (rather than the start) keeps locale-prefixed
    // routes like /fr/cart/add.js working.
    var CART_ADD = /\/cart\/add(\.js)?$/;

    window.fetch = function (input, init) {
      var url;
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof URL) {
        url = input.href;
      } else if (input && typeof input.url === 'string') {
        url = input.url;
      }

      var isCartAdd = false;
      if (typeof url === 'string') {
        try {
          var parsed = new URL(url, window.location.origin);
          isCartAdd =
            parsed.origin === window.location.origin && CART_ADD.test(parsed.pathname);
        } catch (e) {
          isCartAdd = false;
        }
      }

      if (!isCartAdd) {
        return nativeFetch(input, init);
      }

      var result = tail.then(function () {
        return nativeFetch(input, init);
      });

      // Advance the tail when the request settles, or after 10s regardless.
      // A rejection alone can't wedge the queue, but a request that never
      // settles can — a dropped radio mid-request leaves fetch pending for
      // minutes, and without this cap every later add would silently stack
      // behind it, spinning forever. Falling back to unserialized adds is a
      // far better failure than a dead cart.
      tail = Promise.race([
        result.catch(function () {}),
        new Promise(function (resolve) {
          setTimeout(resolve, 10000);
        }),
      ]);

      return result;
    };
  }
})();
